import React from 'react';
import { useSelector } from 'react-redux';
import { DataCard, Select } from '@brighterion/br-ui';
import { FOCUS_VIEW } from 'Common/enums';
import { useTranslation } from 'react-i18next';
import { getUser, shouldShowMerchants, shouldShowReviewConclusions } from 'Selectors';
import { getCustomUserName } from 'Utils';
import useGetLatestRepositoryConfig from 'Hooks/query-hooks/repository/useGetLatestRepositoryConfig';
import useReplaceMerchant from 'Hooks/useReplaceMerchant';
import { DateService } from 'Services';
import clsx from 'clsx';
import styles from '../MerchantSummaryReportView.css';

const focusViewSelect = (
    focusView: AcceptRejectReview,
    setFocusView: (value: AcceptRejectReview) => void,
    t: (text: string, options?: any) => string,
    focusValues: string[],
    size?: 'sm' | 'lg'
) => (
    <Select
        className={styles.focusViewSelectContainer}
        label={t('Focus View')}
        handleClickCallback={(value) => setFocusView(value[0])}
        placeholder='-Select-'
        selectedItems={[focusView]}
        inlineLabel
        size={size}
    >
        {focusValues.map((focusValue) => (
            <option key={focusValue} value={focusValue}>
                {t(focusValue)}
            </option>
        ))}
    </Select>
);

type Props = {
    formValues: ProcessedNewMerchantReportFormValues;
    focusView: AcceptRejectReview;
    setFocusView: (value: AcceptRejectReview) => void;
    createdReportInfo?: CreatedReportInfo;
    newlyGenerated?: boolean;
    users?: UsersHashmap;
};

const HeaderRows = ({ formValues, focusView, setFocusView, createdReportInfo, newlyGenerated, users }: Props) => {
    const { t } = useTranslation();
    const { userId, username } = useSelector(getUser);
    const { dateRangeLabel, dateRange, merchant, currencyEffectiveDate: existingCurrencyEffectiveDate } = formValues;
    const { dateRangeValue, dateRangeType } = dateRange;
    const replacedMerchant = useReplaceMerchant(merchant);
    const { data: repositoryData } = useGetLatestRepositoryConfig();
    const currencyEffectiveDate = DateService.getDateTime(
        existingCurrencyEffectiveDate || repositoryData?.currencyEffectiveDate
    );

    const renderCreatedBy = () => {
        // if the current user is found from users_info table in DB
        // display username from DB, otherwise, display username from mcconect / okta
        if (newlyGenerated) return `${getCustomUserName(userId, users, username)} (${t('me')})`;

        // If the current user is not the creator of the current report and is found from users_info table in DB
        // display username from DB, else display username from the report endpoint
        const reportCreator = createdReportInfo && getCustomUserName(createdReportInfo.createdBy, users);
        return userId === createdReportInfo?.createdBy ? `${reportCreator} (${t('me')})` : `${reportCreator}`;
    };

    return (
        <>
            <div className={styles.inlineHeaderRowWrapper}>
                <div className={clsx(styles.flexRow, styles.inlineReportInfoWrapper)}>
                    {shouldShowMerchants() && (
                        <DataCard
                            dataTestId='report-merchantType-card'
                            className={clsx(styles.reportCard)}
                            key='merchantType'
                            data={replacedMerchant}
                            label={t('Merchant')}
                        />
                    )}
                    <DataCard
                        dataTestId='report-reportType-card'
                        className={clsx(styles.reportCard)}
                        key='reportType'
                        data={shouldShowMerchants() ? t('Merchant Summary') : t('Transaction Summary')}
                        label={t('Report Type')}
                    />
                    <DataCard
                        dataTestId='report-dateRange-card'
                        className={clsx(styles.reportCard)}
                        key='dateRangeType'
                        data={dateRangeType}
                        label={t('Date Range')}
                    />
                    <DataCard
                        dataTestId='report-dates-card'
                        className={clsx(styles.reportCard)}
                        key='dates'
                        data={DateService.getReportDateRange(dateRangeType, dateRangeValue, dateRangeLabel)}
                        label={t('Dates')}
                    />
                    <DataCard
                        dataTestId='report-createdAt-card'
                        className={clsx(styles.reportCard)}
                        key='created'
                        data={newlyGenerated ? DateService.getDateTime(`${new Date()}`) : createdReportInfo?.createdAt}
                        label={newlyGenerated ? t('Generated') : t('Created')}
                    />
                    <DataCard
                        dataTestId='report-createdBy-card'
                        className={clsx(styles.reportCard)}
                        key='createdBy'
                        data={renderCreatedBy()}
                        label={newlyGenerated ? t('Generated By') : t('Created By')}
                    />
                    <DataCard
                        dataTestId='currency-effective-date-card'
                        className={clsx(styles.reportCard)}
                        key='currencyEffectiveDate'
                        data={currencyEffectiveDate}
                        label={t('Currency Effective Date')}
                    />
                </div>
            </div>
            <div className={styles.inlineHeaderRowWrapper}>
                <span className={styles.roundedMsg}>{t('Percent rounded to the nearest 100th')}</span>
                {focusViewSelect(
                    focusView,
                    setFocusView,
                    t,
                    [
                        FOCUS_VIEW.ACCEPT,
                        FOCUS_VIEW.REJECT,
                        ...(shouldShowReviewConclusions() ? [FOCUS_VIEW.REVIEW] : [])
                    ],
                    'sm'
                )}
            </div>
        </>
    );
};

export default HeaderRows;
